import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Home, Phone, Mail, Check, X, CheckCircle2, RotateCcw, CalendarDays, AlertTriangle, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { formatApiError } from '@/utils/apiError';

const STATUS = {
  pending: ['Pending', 'bg-amber-100 text-amber-800'],
  confirmed: ['Confirmed', 'bg-sky-100 text-sky-800'],
  completed: ['Completed', 'bg-emerald-100 text-emerald-800'],
  cancelled: ['Cancelled', 'bg-gray-100 text-gray-600'],
};
const BRAND_COLOUR = { probound: '#2F5E96', proepoxy: '#7A0B7A', prodecor: '#D5AB09' };
const FILTERS = [['open', 'Pending & confirmed'], ['pending', 'Pending'], ['confirmed', 'Confirmed'], ['completed', 'Completed'], ['cancelled', 'Cancelled'], ['all', 'All']];

const when = (c) => new Date(`${c.requested_date}T${c.requested_time}:00`);
const fmt = (c) => when(c).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

export default function StaffMarketingCalls() {
  const navigate = useNavigate();
  const { isStaff } = useContext(AuthContext);
  const [calls, setCalls] = useState(null);
  const [filter, setFilter] = useState('open');
  const [params] = useSearchParams();
  const highlight = params.get('highlight');

  useEffect(() => {
    if (!calls || !highlight) return;
    const el = document.querySelector(`[data-testid="call-${highlight}"]`);
    if (el) { el.scrollIntoView({ block: 'center' }); el.classList.add('ring-2', 'ring-sky-500'); }
  }, [calls, highlight]);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/staff/strategy-calls`, { credentials: 'include' });
      const d = await res.json().catch(() => []);
      if (!res.ok) throw new Error(formatApiError(d) || 'Failed to load');
      setCalls(d);
    } catch (e) { toast.error(e.message); }
  }, []);
  useEffect(() => { if (isStaff) load(); }, [isStaff, load]);

  const rows = useMemo(() => {
    if (!calls) return [];
    const list = filter === 'all' ? calls : filter === 'open' ? calls.filter((c) => ['pending', 'confirmed'].includes(c.status)) : calls.filter((c) => c.status === filter);
    return [...list].sort((a, b) => (filter === 'open' || filter === 'pending' || filter === 'confirmed' ? when(a) - when(b) : when(b) - when(a)));
  }, [calls, filter]);

  const update = async (c, status, staff_notes = c.staff_notes || '') => {
    if (status === 'confirmed' && c.clash && !window.confirm(`Another open call is already booked for ${fmt(c)}.\n\nConfirm this one anyway?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/staff/strategy-calls/${c.id}`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status, staff_notes }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(d) || 'Update failed');
      const { notified, ...rest } = d;
      setCalls((cs) => cs.map((x) => (x.id === c.id ? { ...x, ...rest } : x)));
      if (notified) {
        const sent = [notified.sms && 'text', notified.email && 'email'].filter(Boolean);
        if (sent.length) toast.success(`Confirmed — ${sent.join(' + ')} sent to ${c.name}`);
        if (notified.errors?.length) toast.error(`Confirmed, but ${notified.errors.join(' · ')}`, { duration: 8000 });
      } else if (status !== c.status) toast.success(`Marked ${STATUS[status][0].toLowerCase()}`);
    } catch (e) { toast.error(e.message); }
  };

  const counts = useMemo(() => ({ pending: calls?.filter((c) => c.status === 'pending').length || 0, confirmed: calls?.filter((c) => c.status === 'confirmed').length || 0, completed: calls?.filter((c) => c.status === 'completed').length || 0 }), [calls]);

  if (!isStaff) return null;
  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <div className="text-white" style={{ background: 'linear-gradient(135deg,#0B0B0E 0%,#1e3a5f 75%)', borderBottom: '3px solid #60a5fa' }}>
        <div className="max-w-[1200px] mx-auto px-3 sm:px-4 py-4 sm:py-5 flex items-center gap-2 sm:gap-3">
          <button onClick={() => navigate('/staff')} className="p-2 hover:bg-white/10 rounded-lg shrink-0" aria-label="Back" data-testid="calls-back"><ArrowLeft className="w-5 h-5" /></button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-2xl font-bold flex items-center gap-2 truncate"><Phone className="w-5 h-5 sm:w-6 sm:h-6 text-sky-300" /> Marketing Calls</h1>
            <p className="hidden sm:block text-white/70 text-sm mt-0.5">Installers who've pressed "Book My Marketing Call" — press Confirm and they get a text + email locking the slot in. Calls also show on your calendar.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/calendar')} className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="calls-calendar"><CalendarDays className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Calendar</span></Button>
          <Button variant="outline" size="sm" onClick={() => navigate('/staff')} className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="calls-home"><Home className="w-4 h-4" /></Button>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-3 sm:px-4 py-5 space-y-4">
        <div className="grid grid-cols-3 gap-3" data-testid="calls-stats">
          <Stat label="Awaiting confirmation" value={counts.pending} tone="text-amber-700" onClick={() => setFilter('pending')} testId="stat-pending" />
          <Stat label="Confirmed · upcoming" value={counts.confirmed} tone="text-sky-700" onClick={() => setFilter('confirmed')} testId="stat-confirmed" />
          <Stat label="Completed" value={counts.completed} tone="text-emerald-700" onClick={() => setFilter('completed')} testId="stat-completed" />
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="calls-filters">
          {FILTERS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`calls-filter-${k}`} className={`px-3 py-1.5 rounded-full text-sm font-semibold border-2 ${filter === k ? 'bg-[#1e3a5f] text-white border-transparent' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'}`}>{l}</button>
          ))}
        </div>

        {!calls ? <Card className="p-10 text-center text-gray-500">Loading…</Card> : rows.length === 0 ? (
          <Card className="p-10 text-center text-gray-500" data-testid="calls-empty">No marketing calls in this view.</Card>
        ) : (
          <div className="space-y-3" data-testid="calls-list">
            {rows.map((c) => <CallCard key={c.id} call={c} onUpdate={update} navigate={navigate} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function CallCard({ call: c, onUpdate, navigate }) {
  const [notes, setNotes] = useState(c.staff_notes || '');
  const [label, cls] = STATUS[c.status] || STATUS.pending;
  const past = when(c) < new Date();
  const open = ['pending', 'confirmed'].includes(c.status);
  return (
    <Card className="p-4" data-testid={`call-${c.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded text-white" style={{ background: BRAND_COLOUR[c.brand] || '#6B7280' }}>{c.brand}</span>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${cls}`} data-testid={`call-status-${c.id}`}>{label}</span>
            {open && past && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 whitespace-nowrap">slot has passed</span>}
            {c.clash && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-orange-100 text-orange-800" data-testid={`call-clash-${c.id}`}><AlertTriangle className="w-3 h-3" /> same slot as another call</span>}
            {c.confirmation_sent && <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700" data-testid={`call-notified-${c.id}`}><MessageSquare className="w-3 h-3" /> installer notified{c.confirmation_sent.sms && c.confirmation_sent.email ? ' (text + email)' : c.confirmation_sent.sms ? ' (text)' : c.confirmation_sent.email ? ' (email)' : ' — failed'}</span>}
          </div>
          <div className="mt-1.5 font-bold text-gray-900 flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-gray-400 shrink-0" /> <span>{fmt(c)}</span></div>
          <div className="text-sm text-gray-800 mt-1 break-words">
            <button type="button" onClick={() => c.user?.user_id && navigate(`/staff/installers/${c.user.user_id}`)} className="font-semibold hover:underline" data-testid={`call-name-${c.id}`}>{c.name}</button>
            {c.company && <span className="text-gray-500"> · {c.company}</span>}
            {c.user?.account_code && <span className="text-gray-400"> · {c.user.account_code}</span>}
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600 mt-1">
            {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 hover:text-gray-900 whitespace-nowrap"><Phone className="w-3 h-3" /> {c.phone}</a>}
            {c.email && <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 hover:text-gray-900 break-all"><Mail className="w-3 h-3 shrink-0" /> {c.email}</a>}
          </div>
          {c.notes && <p className="mt-2 text-sm text-gray-700 bg-gray-50 border rounded-lg px-3 py-2 whitespace-pre-wrap break-words"><span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 block">Installer's note</span>{c.notes}</p>}
        </div>
        <div className="flex flex-wrap gap-1.5 sm:shrink-0 sm:justify-end">
          {c.status === 'pending' && <Button size="sm" onClick={() => onUpdate(c, 'confirmed', notes)} className="flex-1 sm:flex-none bg-sky-700 hover:bg-sky-800" data-testid={`call-confirm-${c.id}`}><Check className="w-4 h-4 mr-1" /> Confirm</Button>}
          {open && <Button size="sm" onClick={() => onUpdate(c, 'completed', notes)} className="flex-1 sm:flex-none bg-emerald-700 hover:bg-emerald-800" data-testid={`call-complete-${c.id}`}><CheckCircle2 className="w-4 h-4 mr-1" /> Done</Button>}
          {open && <Button size="sm" variant="outline" onClick={() => onUpdate(c, 'cancelled', notes)} className="flex-1 sm:flex-none" data-testid={`call-cancel-${c.id}`}><X className="w-4 h-4 mr-1" /> Cancel</Button>}
          {!open && <Button size="sm" variant="outline" onClick={() => onUpdate(c, 'pending', notes)} className="flex-1 sm:flex-none" data-testid={`call-reopen-${c.id}`}><RotateCcw className="w-4 h-4 mr-1" /> Reopen</Button>}
        </div>
      </div>
      <div className="mt-3">
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={() => notes !== (c.staff_notes || '') && onUpdate(c, c.status, notes)} rows={2} placeholder="Staff notes — what was discussed, what they're buying, follow-ups… (saves when you click away)" className="text-sm bg-white" data-testid={`call-notes-${c.id}`} />
      </div>
    </Card>
  );
}

function Stat({ label, value, tone, onClick, testId }) {
  return (
    <Card className="p-4 cursor-pointer hover:shadow-md" onClick={onClick} data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">{label}</div>
      <div className={`text-2xl font-bold ${tone}`}>{value}</div>
    </Card>
  );
}
